/**
 * Socket.IO client for the `/ws/admin` namespace.
 *
 * Authentication: the server's AdminGateway middleware reads the admin
 * httpOnly cookie (`dcms_access`) off `socket.handshake.headers.cookie`.
 * Socket.IO's WebSocket transport forwards browser cookies automatically
 * for same-origin connections; the Vite dev proxy is configured to
 * tunnel `/ws` (see `vite.config.ts`), so the admin cookie is set on the
 * same origin and round-tripped without `withCredentials`. We pass it
 * anyway because Socket.IO's `polling` fallback uses XHR, which DOES
 * require `withCredentials: true` to send cookies.
 *
 * Event payloads use the discriminated-union shapes from
 * `@displaycms/contracts.ServerToAdminEvent`. The server emits them
 * keyed by event NAME (e.g. `player.online`) with the full typed
 * payload as the second argument — verified in
 * `apps/server/src/ws/ws-broadcaster.service.ts`.
 *
 * The client is a singleton, lazily constructed on first `connect()`,
 * because every <PlayersListPage> mount-unmount cycle should reuse the
 * same connection. Listener arrays are reference-counted: callers get
 * an `off` function back from each `onX(cb)` registration.
 */

import { io, type Socket } from 'socket.io-client';
import type {
  AdminWsAuditEventCreated,
  AdminWsPlayerDown,
  AdminWsPlayerOffline,
  AdminWsPlayerOnline,
  AdminWsPlayerStatusUpdated,
  AdminWsVerificationFailedAlert,
  CommandStatus,
} from '@displaycms/contracts';
import type { z } from 'zod';

type PlayerOnlinePayload = z.infer<typeof AdminWsPlayerOnline>;
type PlayerOfflinePayload = z.infer<typeof AdminWsPlayerOffline>;
/** SP2: an active screen has been unreachable past PLAYER_DOWN_MS (a real outage). */
type PlayerDownPayload = z.infer<typeof AdminWsPlayerDown>;
type PlayerStatusUpdatedPayload = z.infer<typeof AdminWsPlayerStatusUpdated>;
type AuditEventCreatedPayload = z.infer<typeof AdminWsAuditEventCreated>;
/**
 * Verification-failures last mile: server pushes `verification.failed.alert`
 * on every persisted ingest batch (security-relevant — a player's Ed25519 /
 * sha256 / expiry / revocation check failed against served bytes).
 */
type VerificationFailedAlertPayload = z.infer<typeof AdminWsVerificationFailedAlert>;
export type VerificationFailedAlertEvent = VerificationFailedAlertPayload;
/**
 * Admin-namespace push for operator-command lifecycle events (P5 W1).
 * Re-exported as `CommandStatusEvent` so call sites don't have to know
 * about the contracts-package zod alias.
 */
export type CommandStatusEvent = z.infer<typeof CommandStatus>;

export type PlayerOnlineCallback = (e: PlayerOnlinePayload) => void;
export type PlayerOfflineCallback = (e: PlayerOfflinePayload) => void;
export type PlayerDownCallback = (e: PlayerDownPayload) => void;
export type PlayerStatusUpdatedCallback = (e: PlayerStatusUpdatedPayload) => void;
export type AuditEventCreatedCallback = (e: AuditEventCreatedPayload) => void;
export type CommandStatusCallback = (e: CommandStatusEvent) => void;
export type VerificationFailedAlertCallback = (e: VerificationFailedAlertPayload) => void;

interface AdminWsClient {
  connect(): void;
  disconnect(): void;
  isConnected(): boolean;
  onPlayerOnline(cb: PlayerOnlineCallback): () => void;
  onPlayerOffline(cb: PlayerOfflineCallback): () => void;
  /** SP2: subscribe to `player.down` (active screen unreachable past the outage threshold). */
  onPlayerDown(cb: PlayerDownCallback): () => void;
  onPlayerStatusUpdated(cb: PlayerStatusUpdatedCallback): () => void;
  onAuditEventCreated(cb: AuditEventCreatedCallback): () => void;
  /** Subscribe to `command.status` server pushes (P5 W1). */
  onCommandStatus(cb: CommandStatusCallback): () => void;
  /** Subscribe to `verification.failed.alert` server pushes (security alerts). */
  onVerificationFailedAlert(cb: VerificationFailedAlertCallback): () => void;
}

const ENDPOINT_NAMESPACE = '/ws/admin';

function defaultBaseUrl(): string {
  // In the browser, same-origin connections work as long as the dev
  // proxy or Caddy forwards /ws to the server. socket.io-client
  // accepts an empty string and uses `location.origin`.
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
}

function createClient(): AdminWsClient {
  let socket: Socket | null = null;

  const playerOnlineCbs = new Set<PlayerOnlineCallback>();
  const playerOfflineCbs = new Set<PlayerOfflineCallback>();
  const playerDownCbs = new Set<PlayerDownCallback>();
  const playerStatusUpdatedCbs = new Set<PlayerStatusUpdatedCallback>();
  const auditEventCreatedCbs = new Set<AuditEventCreatedCallback>();
  const commandStatusCbs = new Set<CommandStatusCallback>();
  const verificationFailedAlertCbs = new Set<VerificationFailedAlertCallback>();

  const bindHandlers = (s: Socket): void => {
    s.on('player.online', (payload: PlayerOnlinePayload) => {
      playerOnlineCbs.forEach((cb) => {
        try {
          cb(payload);
        } catch {
          // never let a listener throw take down the socket
        }
      });
    });
    s.on('player.offline', (payload: PlayerOfflinePayload) => {
      playerOfflineCbs.forEach((cb) => {
        try {
          cb(payload);
        } catch {
          // ignore
        }
      });
    });
    s.on('player.down', (payload: PlayerDownPayload) => {
      playerDownCbs.forEach((cb) => {
        try {
          cb(payload);
        } catch {
          // ignore
        }
      });
    });
    s.on('player.status.updated', (payload: PlayerStatusUpdatedPayload) => {
      playerStatusUpdatedCbs.forEach((cb) => {
        try {
          cb(payload);
        } catch {
          // ignore
        }
      });
    });
    s.on('audit.event.created', (payload: AuditEventCreatedPayload) => {
      auditEventCreatedCbs.forEach((cb) => {
        try {
          cb(payload);
        } catch {
          // ignore
        }
      });
    });
    s.on('command.status', (payload: CommandStatusEvent) => {
      commandStatusCbs.forEach((cb) => {
        try {
          cb(payload);
        } catch {
          // ignore
        }
      });
    });
    s.on('verification.failed.alert', (payload: VerificationFailedAlertPayload) => {
      verificationFailedAlertCbs.forEach((cb) => {
        try {
          cb(payload);
        } catch {
          // ignore
        }
      });
    });
  };

  const connect = (): void => {
    if (socket && socket.connected) return;
    if (socket) {
      // Existing socket but disconnected — try to reopen.
      socket.connect();
      return;
    }
    socket = io(`${defaultBaseUrl()}${ENDPOINT_NAMESPACE}`, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      // Reconnect with backoff — first retry at 500ms, capped at 10s.
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 10_000,
      // Sockets are bidirectional but P1 admin clients only listen.
      autoConnect: true,
    });
    bindHandlers(socket);
  };

  const disconnect = (): void => {
    if (!socket) return;
    socket.disconnect();
    socket.removeAllListeners();
    socket = null;
  };

  const isConnected = (): boolean => socket !== null && socket.connected;

  const onPlayerOnline = (cb: PlayerOnlineCallback): (() => void) => {
    playerOnlineCbs.add(cb);
    return () => {
      playerOnlineCbs.delete(cb);
    };
  };
  const onPlayerOffline = (cb: PlayerOfflineCallback): (() => void) => {
    playerOfflineCbs.add(cb);
    return () => {
      playerOfflineCbs.delete(cb);
    };
  };
  const onPlayerDown = (cb: PlayerDownCallback): (() => void) => {
    playerDownCbs.add(cb);
    return () => {
      playerDownCbs.delete(cb);
    };
  };
  const onPlayerStatusUpdated = (
    cb: PlayerStatusUpdatedCallback,
  ): (() => void) => {
    playerStatusUpdatedCbs.add(cb);
    return () => {
      playerStatusUpdatedCbs.delete(cb);
    };
  };
  const onAuditEventCreated = (
    cb: AuditEventCreatedCallback,
  ): (() => void) => {
    auditEventCreatedCbs.add(cb);
    return () => {
      auditEventCreatedCbs.delete(cb);
    };
  };
  const onCommandStatus = (cb: CommandStatusCallback): (() => void) => {
    commandStatusCbs.add(cb);
    return () => {
      commandStatusCbs.delete(cb);
    };
  };
  const onVerificationFailedAlert = (
    cb: VerificationFailedAlertCallback,
  ): (() => void) => {
    verificationFailedAlertCbs.add(cb);
    return () => {
      verificationFailedAlertCbs.delete(cb);
    };
  };

  return {
    connect,
    disconnect,
    isConnected,
    onPlayerOnline,
    onPlayerOffline,
    onPlayerDown,
    onPlayerStatusUpdated,
    onAuditEventCreated,
    onCommandStatus,
    onVerificationFailedAlert,
  };
}

// Module-level singleton. Tests that need a fresh client can call
// `__resetAdminWsForTests()`.
let singleton: AdminWsClient = createClient();

export function getAdminWs(): AdminWsClient {
  return singleton;
}

/** Test-only: rebuild the singleton so its listener sets are empty. */
export function __resetAdminWsForTests(): void {
  try {
    singleton.disconnect();
  } catch {
    // ignore
  }
  singleton = createClient();
}

// Convenience re-exports — most call sites only need the listeners.
export const adminWs = {
  connect: (): void => singleton.connect(),
  disconnect: (): void => singleton.disconnect(),
  isConnected: (): boolean => singleton.isConnected(),
  onPlayerOnline: (cb: PlayerOnlineCallback): (() => void) =>
    singleton.onPlayerOnline(cb),
  onPlayerOffline: (cb: PlayerOfflineCallback): (() => void) =>
    singleton.onPlayerOffline(cb),
  onPlayerDown: (cb: PlayerDownCallback): (() => void) =>
    singleton.onPlayerDown(cb),
  onPlayerStatusUpdated: (cb: PlayerStatusUpdatedCallback): (() => void) =>
    singleton.onPlayerStatusUpdated(cb),
  onAuditEventCreated: (cb: AuditEventCreatedCallback): (() => void) =>
    singleton.onAuditEventCreated(cb),
  onCommandStatus: (cb: CommandStatusCallback): (() => void) =>
    singleton.onCommandStatus(cb),
  onVerificationFailedAlert: (cb: VerificationFailedAlertCallback): (() => void) =>
    singleton.onVerificationFailedAlert(cb),
};
